import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { DescriptionText } from '~/eds/src/components/DescriptionText/DescriptionText';
import { FormField } from '~/eds/src/components/FormField/FormField';
import { useToast } from '~/eds/src/components/hooks/useToast';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { Modal } from '~/eds/src/components/Modal/Modal';
import { Panel } from '~/eds/src/components/Panel/Panel';
import { SortableList } from '~/eds/src/components/SortableList/SortableList';
import { Text } from '~/eds/src/components/Text/Text';
import { TextInput } from '~/eds/src/components/TextInput/TextInput';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { api } from '~/redux/api/createApi';
import { slices } from '~/redux/slices';

import { DataFieldSection } from './types';

export const SectionModal = ({ clientId }: { clientId: string }) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const { toast } = useToast();
  const enableModalConsolidation = useFlag(FlagType.ModalConsolidation);

  const action = useSelector(slices.dataFields.selectors.selectAction);
  const section = useSelector(slices.dataFields.selectors.selectedSection);
  const sections = useSelector(slices.dataFields.selectors.selectSections);

  const [sectionName, setSectionName] = useState('');
  const [deleteValue, setDeleteValue] = useState('');
  const [sectionOrder, setSectionOrder] = useState<DataFieldSection[]>([]);

  useEffect(() => {
    if (action === 'rename' && section) {
      setSectionName(section.label ?? '');
    } else if (action === 'add') {
      setSectionName('');
    }
    if (action === 'reorder') {
      setSectionOrder(sections);
    }
  }, [action, section, sections]);

  const [createClientSectionData, { isLoading: isCreatingSection }] =
    api.endpoints.createClientSectionData.useMutation();

  const [updateClientSectionName, { isLoading: isRenamingSection }] =
    api.endpoints.updateClientSectionName.useMutation();

  const [deleteClientSection, { isLoading: isDeletingSection }] =
    api.endpoints.deleteClientSection.useMutation();

  const [reorderClientSectionsData, { isLoading: isReorderingSections }] =
    api.endpoints.reorderClientSectionsData.useMutation();

  const items = sectionOrder.map((section: DataFieldSection) => ({
    itemSettings: {
      enableBorder: true,
    },
    item: {
      data: section,
    },
  }));

  const onHide = () => {
    dispatch(slices.dataFields.actions.resetAction());
    setSectionName('');
    setDeleteValue('');
    setSectionOrder([]);
  };

  const onConfirm = () => {
    if (action === 'reorder') {
      reorderClientSectionsData({
        clientId,
        sortOrder: sectionOrder?.map(
          (section: DataFieldSection, index: number) => {
            return { id: section.id, position: index };
          },
        ),
      })
        ?.unwrap()
        .then(() => {
          toast({
            message: intl.formatMessage({
              id: 'DataFields.sectionOrderUpdatedSuccess',
              defaultMessage: 'Section orders have been updated.',
              description: 'Success message when section order is updated',
            }),
            status: 'success',
          });
          dispatch(slices.dataFields.actions.resetAction());
        })
        .catch((err) => {
          toast({
            message:
              err?.message ??
              intl.formatMessage({
                id: 'DataFields.sectionOrderUpdateError',
                defaultMessage:
                  'An error occurred while updating the section order.',
                description: 'Error message when section order update fails',
              }),
            status: 'danger',
          });
        });
    }
    if (action === 'rename') {
      if (!section?.id) {
        return null;
      }

      updateClientSectionName({
        label: sectionName,
        id: section.id,
      })
        ?.unwrap()
        .then(() => {
          toast({
            message: intl.formatMessage({
              id: 'DataFields.sectionRenamedSuccess',
              defaultMessage: 'Section has been renamed.',
              description: 'Success message when section is renamed',
            }),
            status: 'success',
          });
          dispatch(slices.dataFields.actions.resetConfiguration());
        })
        .catch(() => {
          toast({
            message: intl.formatMessage({
              id: 'DataFields.sectionRenameError',
              defaultMessage: 'An error occurred while renaming the section.',
              description: 'Error message when section rename fails',
            }),
            status: 'danger',
          });
        });
    }
    if (action === 'add') {
      createClientSectionData({
        label: sectionName,
        client_id: clientId,
      })
        ?.unwrap()
        .then(() => {
          toast({
            message: intl.formatMessage({
              id: 'DataFields.sectionCreatedSuccess',
              defaultMessage: 'New section has been added.',
              description: 'Success message when section is created',
            }),
            status: 'success',
          });
          dispatch(slices.dataFields.actions.resetAction());
        })
        .catch((error) => {
          toast({
            message:
              (error as any)?.data?.label ??
              intl.formatMessage({
                id: 'DataFields.sectionCreateError',
                defaultMessage: 'An error occurred while creating the section.',
                description: 'Error message when section creation fails',
              }),
            status: 'danger',
          });
        });
    }
    if (action === 'delete') {
      if (!section) {
        return null;
      }

      deleteClientSection({ sectionId: section.id })
        ?.unwrap()
        .then(() => {
          toast({
            message: intl.formatMessage({
              id: 'DataFields.sectionDeletedSuccess',
              defaultMessage: 'Section has been deleted.',
              description: 'Success message when section is deleted',
            }),
            status: 'success',
          });
          dispatch(slices.dataFields.actions.resetAction());
        })
        .catch(() => {
          toast({
            message: intl.formatMessage({
              id: 'DataFields.sectionDeleteError',
              defaultMessage: 'An error occurred while deleting the section.',
              description: 'Error message when section deletion fails',
            }),
            status: 'danger',
          });
        });
    }
  };

  const reOrderContent = (
    <Layout preset="sections">
      <DescriptionText
        text={intl.formatMessage({
          id: 'ReorderSectionsModal.instructions',
          defaultMessage:
            'Use the drag handles to reorder the list of sections.',
          description: 'Instructions for reordering sections',
        })}
      />
      <SortableList
        renderTitle={(item: { data: DataFieldSection }) => item.data.label}
        items={items}
        rowSpacing="s"
        itemConfig={{
          enableBorder: true,
          padding: 's',
        }}
        onChange={(updatedItems) => {
          setSectionOrder(updatedItems.map((item) => item.data) ?? []);
        }}
      />
    </Layout>
  );

  if (!action) return null;
  if (action === 'reorder') {
    return enableModalConsolidation ? (
      <Panel
        enableBackdrop
        mode="floating"
        width="560px"
        title={intl.formatMessage({
          id: 'ReorderSectionsModal.title',
          defaultMessage: 'Reorder Sections',
          description: 'Modal title for reordering sections',
        })}
        footer={{
          actions: [
            {
              mode: 'chip',
              text: intl.formatMessage({
                id: 'DataFields.cancelButton',
                defaultMessage: 'Cancel',
                description: 'Cancel button text',
              }),
              onClick: onHide,
            },
            {
              mode: 'chip',
              level: 'primary',
              text: intl.formatMessage({
                id: 'DataFields.saveOrderButton',
                defaultMessage: 'Save Order',
                description: 'Save button text',
              }),
              onClick: onConfirm,
              isLoading: isReorderingSections,
            },
          ],
        }}
        hidden={{ onHide }}
        children={reOrderContent}
      />
    ) : (
      <Modal
        title={intl.formatMessage({
          id: 'ReorderSectionsModal.title',
          defaultMessage: 'Reorder Sections',
          description: 'Modal title for reordering sections',
        })}
        isVisible={true}
        children={reOrderContent}
        primaryAction={{
          text: intl.formatMessage({
            id: 'MoveFieldToSectionModal.saveOrderButton',
            defaultMessage: 'Save Order',
            description: 'Save order button text',
          }),
          onClick: onConfirm,
          isLoading: isReorderingSections,
        }}
        onCancel={onHide}
        onHide={onHide}
      />
    );
  }
  if (action === 'add' || action === 'rename') {
    const isRename = action === 'rename';

    const title = isRename
      ? intl.formatMessage({
          id: 'DataFields.renameSectionTitle',
          defaultMessage: 'Rename Section',
          description: 'Rename section modal title',
        })
      : intl.formatMessage({
          id: 'DataFields.newSectionTitle',
          defaultMessage: 'New Section',
          description: 'New section modal title',
        });

    const primaryActionText = isRename
      ? intl.formatMessage({
          id: 'DataFields.saveButton',
          defaultMessage: 'Save',
          description: 'Save button text',
        })
      : intl.formatMessage({
          id: 'DataFields.createSectionButton',
          defaultMessage: 'Create Section',
          description: 'Create section button text',
        });

    return enableModalConsolidation ? (
      <Panel
        enableBackdrop
        mode="floating"
        width="560px"
        title={title}
        hidden={{ onHide }}
        footer={{
          actions: [
            {
              mode: 'chip',
              text: intl.formatMessage({
                id: 'DataFields.cancelButton',
                defaultMessage: 'Cancel',
                description: 'Cancel button text',
              }),
              onClick: onHide,
            },
            {
              mode: 'chip',
              text: primaryActionText,
              isLoading: isCreatingSection || isRenamingSection,
              level: 'primary',
              onClick: onConfirm,
            },
          ],
        }}
        children={
          <FormField<string, false>
            input={TextInput}
            label={intl.formatMessage({
              id: 'DataFields.sectionNameLabel',
              defaultMessage: 'Section Name',
              description: 'Section name form field label',
            })}
            name="Section name"
            value={sectionName}
            onChange={(value) => setSectionName(value ?? '')}
          />
        }
      />
    ) : (
      <Modal
        title={title}
        isVisible={true}
        children={
          <FormField<string, false>
            input={TextInput}
            label={intl.formatMessage({
              id: 'DataFields.sectionNameLabel',
              defaultMessage: 'Section Name',
              description: 'Section name form field label',
            })}
            name="Section name"
            value={sectionName}
            onChange={(value) => setSectionName(value ?? '')}
          />
        }
        primaryAction={{
          isLoading: isCreatingSection || isRenamingSection,
          text: primaryActionText,
          level: 'secondary',
          onClick: onConfirm,
        }}
        onCancel={onHide}
        onHide={onHide}
      />
    );
  } else if (action === 'delete') {
    return (
      <Modal
        children={
          <Layout preset="form-fields">
            <Text>
              {intl.formatMessage(
                {
                  id: 'DataFields.deleteSectionConfirmation',
                  defaultMessage:
                    'Are you sure you want to delete {sectionName}? All of the data fields inside the section will also be deleted. You will not be able to undo this action.',
                  description:
                    'Delete section confirmation message. Example: sectionName = "Contract Details"',
                },
                {
                  sectionName: section?.label,
                },
              )}
            </Text>
            <FormField<string, false>
              input={TextInput}
              label={intl.formatMessage({
                id: 'DataFields.deleteConfirmationLabel',
                defaultMessage: "Type in the word 'DELETE' to confirm",
                description: 'Delete confirmation form field label',
              })}
              name="Section name"
              value={deleteValue}
              onChange={(value) => setDeleteValue(value ?? '')}
            />
          </Layout>
        }
        primaryAction={{
          text: intl.formatMessage({
            id: 'DataFields.deleteButton',
            defaultMessage: 'Delete',
            description: 'Delete button text',
          }),
          level: 'danger',
          isLoading: isDeletingSection,
          disabled: deleteValue !== 'DELETE',
          onClick: onConfirm,
        }}
        onCancel={onHide}
        onHide={onHide}
        isVisible={true}
        title={intl.formatMessage({
          id: 'DeleteSectionModal.title',
          defaultMessage: 'Delete Section',
          description: 'Modal title for deleting section',
        })}
      />
    );
  }
};
